import { useQuery, useMutation, useQueryClient, UseQueryOptions } from '@tanstack/react-query';
import adsManagerService, {
  FetchAdsParams,
  BatchStatusRequest,
  BatchBudgetRequest,
} from '@/services/adsManager.service';
import { Ad } from '@/types/ads.types';

/**
 * Query Keys for React Query caching
 * Organized by feature for easy invalidation
 */
export const adsManagerKeys = {
  all: ['adsManager'] as const,
  ads: (params: FetchAdsParams) => ['adsManager', 'ads', params] as const,
  creatives: (creativeIds: string[]) => ['adsManager', 'creatives', creativeIds] as const,
};

/**
 * Hook: useAdsManagerData
 * Fetches unified data set (UDS) with automatic caching and deduplication
 *
 * @param params - Query parameters (fromDate, toDate, enrich, etc.)
 * @param options - React Query options for customization
 */
export const useAdsManagerData = (
  params: FetchAdsParams,
  options?: Omit<UseQueryOptions<Ad[]>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: adsManagerKeys.ads(params),
    queryFn: () => adsManagerService.fetchAds(params),
    // Only fetch if we have required params
    enabled: Boolean(params.fromDate && params.toDate) && (options?.enabled !== false),
    staleTime: 1000 * 60 * 2, // Consider data fresh for 2 minutes
    ...options,
  });
};

/**
 * Hook: useCreatives
 * Fetches creative thumbnails with caching
 *
 * @param creativeIds - Array of creative IDs to fetch
 * @param options - React Query options for customization
 */
export const useCreatives = (
  creativeIds: string[],
  options?: Omit<UseQueryOptions<Record<string, string>>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: adsManagerKeys.creatives(creativeIds),
    queryFn: () => adsManagerService.fetchCreatives(creativeIds),
    enabled: creativeIds.length > 0 && (options?.enabled !== false),
    staleTime: 1000 * 60 * 10, // Creative URLs are stable, cache for 10 minutes
    ...options,
  });
};

/**
 * Hook: useUpdateAdStatus
 * Mutation hook for updating ad status
 */
export const useUpdateAdStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ adId, status }: { adId: string; status: 'ACTIVE' | 'PAUSED' }) =>
      adsManagerService.updateAdStatus(adId, status),
    onSuccess: () => {
      // Invalidate ads queries to refetch with updated status
      queryClient.invalidateQueries({ queryKey: adsManagerKeys.all });
    },
  });
};

/**
 * Hook: useBatchUpdateAdStatuses
 * Mutation hook for batch updating ad statuses
 */
export const useBatchUpdateAdStatuses = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: BatchStatusRequest) =>
      adsManagerService.batchUpdateAdStatuses(request),
    onSuccess: () => {
      // Invalidate ads queries to refetch with updated statuses
      queryClient.invalidateQueries({ queryKey: adsManagerKeys.all });
    },
  });
};

/**
 * Hook: useUpdateAdsetBudget
 * Mutation hook for updating adset budget
 */
export const useUpdateAdsetBudget = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ adsetId, budget }: { adsetId: string; budget: number }) =>
      adsManagerService.updateAdsetBudget(adsetId, budget),
    onSuccess: () => {
      // Invalidate ads queries to refetch with updated budget
      queryClient.invalidateQueries({ queryKey: adsManagerKeys.all });
    },
  });
};

/**
 * Hook: useBatchUpdateAdsetBudgets
 * Mutation hook for batch updating adset budgets
 */
export const useBatchUpdateAdsetBudgets = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: BatchBudgetRequest) =>
      adsManagerService.batchUpdateAdsetBudgets(request),
    onSuccess: () => {
      // Invalidate ads queries to refetch with updated budgets
      queryClient.invalidateQueries({ queryKey: adsManagerKeys.all });
    },
  });
};

/**
 * Hook: useSingleUpdateAdStatus
 * Mutation hook for updating a single ad status
 * Use this for inline row updates in the table
 */
export const useSingleUpdateAdStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ adId, status }: { adId: string; status: 'ACTIVE' | 'PAUSED' }) =>
      adsManagerService.updateAdStatus(adId, status),
    onSuccess: () => {
      // Invalidate ads queries to refetch with updated status
      queryClient.invalidateQueries({ queryKey: adsManagerKeys.all });
    },
  });
};

/**
 * Hook: useSingleUpdateAdsetBudget
 * Mutation hook for updating a single adset budget
 * Use this for inline row updates in the table
 */
export const useSingleUpdateAdsetBudget = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ adsetId, budget }: { adsetId: string; budget: number }) =>
      adsManagerService.updateAdsetBudget(adsetId, budget),
    onSuccess: () => {
      // Invalidate ads queries to refetch with updated budget
      queryClient.invalidateQueries({ queryKey: adsManagerKeys.all });
    },
  });
};
